import { useEffect, useState, useCallback, useRef } from 'react';
import { api, ApiError } from '../lib/api';
import type { PublicEvent, MovieOption, FeedbackFlag } from '../../shared/protocol';
import { LEGAL_TERMS_URL, LEGAL_PRIVACY_URL } from '../../shared/protocol';
import { formatWhen, recurrenceText, placeToRoomCode } from '../lib/share';
import { RoomCodeBlock, PcJoinButton } from './Guides';
import { TallyBoard } from './TallyBoard';
import { OptionPicker, type DraftOption } from './OptionPicker';
import { AiImageReview } from './AiImageReview';
import { BakingBar } from './BakingBar';
import { PosterLightbox } from './PosterLightbox';

/**
 * The voter landing — reached via a magic link (?e=) or a personal link
 * (?e=&c=). Shows the event, the options as pick-able poster cards, and the
 * live tally. A voter code (c) lets them actually cast/change a vote and greets
 * them by name; without a code they can still watch the tally. If the host has
 * given this person a suggestion allowance, they also get a "suggest a movie" box.
 */
export function VotePage({ eventId, voterCode }: { eventId: string; voterCode?: string }) {
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [voterName, setVoterName] = useState<string | null>(null);
  const [myVote, setMyVote] = useState<string | undefined>(undefined);
  const [casting, setCasting] = useState<string | null>(null);
  const [voteError, setVoteError] = useState<string | null>(null);
  // suggestion allowance for this personal code (0 = not allowed to suggest)
  const [suggestLimit, setSuggestLimit] = useState(0);
  const [suggestionsUsed, setSuggestionsUsed] = useState(0);
  const [draft, setDraft] = useState<DraftOption>({ title: '' });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  // Not an error: the film was already on the ballot and this became a vote for
  // it. Silence here would read as "nothing happened" on a screen whose count
  // did not move either (THU-135).
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  // parody-poster review modal (a suggester may review/regenerate their own pick)
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // enlarged-poster lightbox (single click in view-only, double click while voting)
  const [lightbox, setLightbox] = useState<MovieOption | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadEvent = useCallback(async () => {
    try {
      const ev = await api.getPublicEvent(eventId);
      setEvent(ev);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Could not load this event.');
    }
  }, [eventId]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  // resolve who the personal code belongs to (greeting + prior vote)
  useEffect(() => {
    if (!voterCode) return;
    let alive = true;
    api
      .whoAmI(eventId, voterCode)
      .then((who) => {
        if (!alive) return;
        setVoterName(who.name);
        setMyVote(who.votedOptionId);
        setSuggestLimit(who.suggestLimit);
        setSuggestionsUsed(who.suggestionsUsed);
      })
      .catch(() => {
        /* invalid code — they can still watch the tally */
      });
    return () => {
      alive = false;
    };
  }, [eventId, voterCode]);

  // live tally refresh — 30s is plenty for voters (keeps Cloudflare free-tier
  // request counts down), and polling pauses entirely while the tab is hidden.
  // Coming back to the tab refreshes immediately so the tally never looks stale.
  useEffect(() => {
    let t: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (t === null) t = setInterval(loadEvent, 30000);
    };
    const stop = () => {
      if (t !== null) {
        clearInterval(t);
        t = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        loadEvent(); // catch up right away after being backgrounded
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadEvent]);

  const cast = async (optionId: string) => {
    if (!voterCode) return;
    setVoteError(null);
    setCasting(optionId);
    try {
      const r = await api.vote(eventId, { v: 1, code: voterCode, optionId });
      setEvent(r.event);
      setMyVote(r.votedOptionId);
      setVoterName(r.voterName);
    } catch (e) {
      setVoteError(e instanceof ApiError ? e.message : 'Could not record your vote.');
    } finally {
      setCasting(null);
    }
  };

  const suggestRemaining = Math.max(0, suggestLimit - suggestionsUsed);

  const submitSuggestion = async () => {
    if (!voterCode) return;
    const title = draft.title.trim();
    if (!title) {
      setSuggestError('Type a movie or show first.');
      return;
    }
    setSuggestError(null);
    setSuggestNote(null);
    setSuggesting(true);
    try {
      const r = await api.suggestOption(eventId, {
        v: 1,
        code: voterCode,
        option: {
          title,
          year: draft.year,
          posterUrl: draft.posterUrl,
          tmdbId: draft.tmdbId,
          overview: draft.overview,
          hint: draft.hint,
        },
      });
      setEvent(r.event);
      // THU-135: the server's count, not `n + 1`. A suggestion that MERGED onto a
      // film already on the ballot adds no option and spends no allowance, so the
      // old optimistic increment made the box claim a suggestion was used that
      // was not — and it stayed wrong until the page was reloaded.
      setSuggestionsUsed(r.suggestionsUsed);
      setMyVote(r.optionId); // a merge moves your vote onto the existing option
      setDraft({ title: '' });
      if (r.merged) {
        const merged = r.event.options.find((o) => o.id === r.optionId);
        setSuggestNote(
          merged
            ? `${merged.title} was already on the ballot — your vote went to it.`
            : 'That one was already on the ballot — your vote went to it.',
        );
      }
    } catch (e) {
      setSuggestError(e instanceof ApiError ? e.message : 'Could not add your suggestion.');
    } finally {
      setSuggesting(false);
    }
  };

  const regenerate = async (optionId: string, feedback: FeedbackFlag[], notes: string) => {
    if (!voterCode) return;
    setReviewError(null);
    try {
      const ev = await api.regeneratePoster(eventId, { v: 1, optionId, code: voterCode, feedback, notes });
      setEvent(ev); // art flips to 'baking'; the poll swaps in the new poster
    } catch (e) {
      setReviewError(e instanceof ApiError ? e.message : 'Could not regenerate the poster.');
    }
  };

  const approve = async (optionId: string) => {
    if (!voterCode) return;
    setReviewError(null);
    try {
      const ev = await api.approveArt(eventId, { v: 1, optionId, code: voterCode });
      setEvent(ev);
      setReviewId(null);
    } catch (e) {
      setReviewError(e instanceof ApiError ? e.message : 'Could not approve the poster.');
    }
  };

  // ballot poster click: while voting, a single click votes and a double-click opens the
  // enlarged poster (so a normal click still casts a vote); view-only → single click zooms.
  const cardClick = (o: MovieOption) => {
    if (!voterCode) {
      setLightbox(o);
      return;
    }
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      setLightbox(o);
      return;
    }
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      cast(o.id);
    }, 260);
  };

  if (loadError) {
    return (
      <div className="card">
        <div className="notice error">{loadError}</div>
        <p className="soft">Double-check the link, or ask whoever invited you to send it again.</p>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="card center">
        <span className="spinner" /> Loading the night…
      </div>
    );
  }

  const recur = recurrenceText(event.recurrence);
  const canVote = Boolean(voterCode);
  const roomCode = placeToRoomCode(event.place) ?? undefined;

  return (
    <div>
      <div className="card marquee bulbs">
        <div className="flex-between">
          <h2 className="display">{event.title}</h2>
          {voterName && <span className="chip">Hi {voterName}! 👋</span>}
        </div>
        <p className="soft">
          {formatWhen(event.startsAt)}
          {recur ? ` · ${recur}` : ''}
        </p>
        <div className="notice info">
          <strong>Where:</strong> {event.place}
        </div>
        {event.note && <p className="muted">{event.note}</p>}
        {/* THU-115, the gap that survives the sign-in gate: an invited guest
            never makes an account, so they never reach TermsGate. A ballot link
            is not a form and there is nothing here to tick, so this is notice
            rather than assent — deliberately weaker, and still better than the
            footer alone, which is browsewrap by any reading. Anything stronger
            means making guests sign in, which is the whole point of not. */}
        <p className="legal-inline">
          By joining a night you agree to our{' '}
          <a href={LEGAL_TERMS_URL} target="_blank" rel="noreferrer noopener">
            Terms
          </a>{' '}
          and{' '}
          <a href={LEGAL_PRIVACY_URL} target="_blank" rel="noreferrer noopener">
            Privacy Policy
          </a>
          .
        </p>
      </div>

      <div className="card">
        <h2>{canVote ? '🗳️ Vote on tonight' : '🍿 On the ballot'}</h2>
        {canVote ? (
          <p className="soft">
            Tap a poster to {myVote ? 'change your pick' : 'cast your vote'}. You can change it any
            time before the night.
          </p>
        ) : (
          <div className="notice warn">
            This is a view-only link. For a personal voting link, ask the host to send you yours.
          </div>
        )}

        <div className="options-grid">
          {event.options.map((o) => {
            const mine = myVote === o.id;
            const busy = casting === o.id;
            const baking = o.art === 'pending' || o.art === 'baking';
            // a suggester may review/regenerate their OWN made-up movie (server enforces)
            const canReview = Boolean(voterName && o.art && o.suggestedByName === voterName);
            return (
              <div
                key={o.id}
                className={`movie-card${mine ? ' selected' : ''}`}
                onClick={() => !busy && cardClick(o)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === 'Enter' ? (canVote ? cast(o.id) : setLightbox(o)) : null
                }
                style={{ cursor: 'pointer' }}
                title={canVote ? 'Click to vote · double-click to enlarge' : 'Click to enlarge'}
              >
                <div
                  className={`poster${baking && o.posterUrl ? ' rebaking' : ''}${
                    baking && !o.posterUrl ? ' baking' : ''
                  }`}
                  style={o.posterUrl ? { backgroundImage: `url(${o.posterUrl})` } : undefined}
                >
                  {!o.posterUrl && (baking ? '🎨' : '🎬')}
                  {baking && (
                    <div className="baking-overlay">
                      <BakingBar compact />
                    </div>
                  )}
                </div>
                <div className="meta">
                  <div className="t">
                    {mine && '✓ '}
                    {o.title}
                  </div>
                  <div className="y">
                    {o.year ?? ''} {busy && <span className="spinner" />}
                  </div>
                  {o.poster?.tagline && <div className="tagline">“{o.poster.tagline}”</div>}
                </div>
                {o.suggestedByName && <div className="by">💡 {o.suggestedByName}</div>}
                {canReview && (
                  <button
                    className="review-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setReviewError(null);
                      setReviewId(o.id);
                    }}
                  >
                    🎨 Review
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {voteError && <div className="notice error">{voteError}</div>}
        {myVote && (
          <div className="notice success" style={{ marginTop: 12 }}>
            Your vote is in! 🎉 Change it anytime by tapping a different poster.
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="display">How will you watch?</h3>
        <div className="pill-row">
          <a
            className="btn secondary small"
            href={`?g=vr${roomCode ? `&room=${encodeURIComponent(roomCode)}` : ''}`}
          >
            🥽 Join in VR
          </a>
          <a
            className="btn secondary small"
            href={`?g=browser${roomCode ? `&room=${encodeURIComponent(roomCode)}` : ''}`}
          >
            🖥 Watch in your browser
          </a>
        </div>
        <a className="btn secondary small" href={`?tab=avatar&night=${encodeURIComponent(eventId)}`}>
          🙂 Make your avatar for the theater
        </a>
        <RoomCodeBlock code={roomCode} />
        {roomCode && <PcJoinButton code={roomCode} />}
      </div>

      {canVote && suggestLimit > 0 && (
        <div className="card">
          <div className="flex-between">
            <h2>💡 Suggest a movie</h2>
            <span className="chip">{suggestRemaining} left</span>
          </div>
          {suggestRemaining > 0 ? (
            <>
              <p className="soft">
                Add one to the ballot — everyone can vote on it right away.
              </p>
              <OptionPicker
                index={0}
                value={draft}
                onChange={setDraft}
                onRemove={() => setDraft({ title: '' })}
                canRemove={false}
                label="Your suggestion"
              />
              {suggestError && <div className="notice error">{suggestError}</div>}
              {suggestNote && <div className="notice info">{suggestNote}</div>}
              <button
                className="btn"
                onClick={submitSuggestion}
                disabled={suggesting || !draft.title.trim()}
              >
                {suggesting ? <span className="spinner" /> : '➕ Add to the ballot'}
              </button>
            </>
          ) : (
            <div className="notice info">You've used all your suggestions for this night. 🎬</div>
          )}
        </div>
      )}

      <div className="card">
        <h2>📊 Live results</h2>
        <TallyBoard event={event} onPosterClick={setLightbox} />
      </div>

      {lightbox && <PosterLightbox option={lightbox} onClose={() => setLightbox(null)} />}

      {reviewId &&
        (() => {
          const opt = event.options.find((o) => o.id === reviewId);
          if (!opt) return null;
          return (
            <AiImageReview
              option={opt}
              busy={opt.art === 'baking' || opt.art === 'pending'}
              error={reviewError}
              onApprove={() => approve(opt.id)}
              onRegenerate={(feedback, notes) => regenerate(opt.id, feedback, notes)}
              onClose={() => setReviewId(null)}
            />
          );
        })()}
    </div>
  );
}
